import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { supabase } from '@/lib/supabase'
import { Sentry } from '@/lib/sentry'
import { useAuth } from '@/hooks/useAuth'
import { getPlanLimits } from '@/lib/stripe'
import type { SubscriptionTier } from '@/lib/stripe'
import { startFreeTrial } from '@/services/stripeService'

const SUPABASE_NO_ROW_ERROR_CODE = 'PGRST116'

function isTransientSubscriptionFetchError(error: { code?: string | null; message?: string | null; details?: string | null }) {
  const code = (error.code ?? '').trim()
  const message = (error.message ?? '').toLowerCase()
  const details = (error.details ?? '').toLowerCase()

  return !code && (message.includes('failed to fetch') || details.includes('failed to fetch'))
}

export interface SubscriptionState {
  planTier: SubscriptionTier
  status: string
  isTrial: boolean
  trialDaysRemaining: number
  isTrialExpired: boolean
  isActive: boolean
  canCreateEdit: boolean
  limits: { users: number; products: number; documents: number; ai_tokens: number }
  loading: boolean
  cancelAtPeriodEnd: boolean
  currentPeriodEnd: string | null
  isAdmin: boolean
  refresh: () => Promise<void>
}


const SubscriptionContext = createContext<SubscriptionState | null>(null)

/**
 * Provider that fetches and shares subscription state across all consumers.
 * Place inside AuthProvider so it can access useAuth().
 */
export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
  const state = useSubscriptionInternal()
  return React.createElement(SubscriptionContext.Provider, { value: state }, children)
}

/**
 * Hook to access the shared subscription state.
 * In the app, reads from the SubscriptionProvider context (single fetch).
 * In tests (no provider), falls back to a standalone fetch via useSubscriptionDirect.
 */
export function useSubscription(): SubscriptionState {
  const ctx = useContext(SubscriptionContext)
  if (ctx !== null) return ctx
  // No provider — this path is hit in tests that renderHook(useSubscription)
  // without wrapping in SubscriptionProvider. Safe because the call pattern
  // is always the same (either always context or always direct).
  throw new Error('useSubscription must be used within a SubscriptionProvider')
}

/**
 * Direct subscription hook for use in tests.
 * Performs its own Supabase fetch — not shared via context.
 */
export { useSubscriptionInternal as useSubscriptionDirect }

// ── Internal implementation (single instance via provider) ──────────

function useSubscriptionInternal(): SubscriptionState {
  const { profile } = useAuth()
  const [sub, setSub] = useState<{
    plan_tier: SubscriptionTier
    status: string
    trial_ends_at: string | null
    cancel_at_period_end: boolean
    current_period_end: string | null
  } | null>(null)
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)

  const orgId = profile?.organization_id

  const fetchSubscription = useCallback(async () => {
    if (!orgId) {
      setLoading(false)
      setFetchError(false)
      return
    }

    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('plan_tier, status, trial_ends_at, cancel_at_period_end, current_period_end')
        .eq('org_id', orgId)
        .maybeSingle()

      if (error) {
        // No subscription row yet is expected for new trial organisations.
        if (error.code === SUPABASE_NO_ROW_ERROR_CODE) {
          setSub(null)
          setFetchError(false)
          return
        }

        if (isTransientSubscriptionFetchError(error)) {
          console.warn('Transient subscription fetch failure:', error)
          Sentry.captureMessage('Transient subscription fetch failure', {
            level: 'warning',
            tags: { area: 'subscription', operation: 'fetch_transient', orgId: orgId ?? 'unknown' },
            extra: {
              errorCode: error.code,
              errorMessage: error.message,
              errorDetails: error.details,
              hint: 'Supabase REST transient network fetch failure',
            },
          })
          setFetchError(false)
          return
        }

        console.error('Failed to fetch subscription:', error)
        Sentry.captureException(error, {
          tags: { area: 'subscription', operation: 'fetch', orgId: orgId ?? 'unknown' },
          extra: {
            errorCode: error.code,
            errorMessage: error.message,
            hint: 'Supabase REST subscription fetch failed',
          },
        })
        setSub(null)
        setFetchError(true)
        return
      }

      setSub((data ?? null) as typeof sub)
      setFetchError(false)
    } catch (error: unknown) {
      if (error instanceof Error) {
        if (error.message.toLowerCase().includes('failed to fetch')) {
          console.warn('Transient subscription fetch failure:', error.message)
          Sentry.captureMessage('Transient subscription fetch failure', {
            level: 'warning',
            tags: { area: 'subscription', operation: 'fetch_unexpected_transient', orgId: orgId ?? 'unknown' },
            extra: {
              errorMessage: error.message,
              hint: 'Network-level fetch failure while loading subscription',
            },
          })
          setFetchError(false)
          return
        }

        console.error('Unexpected subscription fetch error:', error.message, error)
        Sentry.captureException(error, {
          tags: { area: 'subscription', operation: 'fetch_unexpected', orgId: orgId ?? 'unknown' },
        })
      } else {
        console.error('Unexpected subscription fetch error:', error)
        Sentry.captureMessage('Unexpected non-Error subscription fetch failure', {
          level: 'error',
          tags: { area: 'subscription', operation: 'fetch_unexpected', orgId: orgId ?? 'unknown' },
          extra: { rawError: String(error) },
        })
      }
      setSub(null)
      setFetchError(true)
    } finally {
      setLoading(false)
    }
  }, [orgId])

  useEffect(() => {
    fetchSubscription()
  }, [fetchSubscription])

  // Safety net: auto-start trial if org exists but no subscription row found
  const trialInitAttempted = useRef(false)
  const prevOrgId = useRef(orgId)
  if (prevOrgId.current !== orgId) {
    prevOrgId.current = orgId
    trialInitAttempted.current = false
  }
  useEffect(() => {
    if (loading || fetchError || sub || !orgId || trialInitAttempted.current) return
    trialInitAttempted.current = true
    startFreeTrial(orgId)
      .then(() => fetchSubscription())
      .catch((err) => {
        console.error('Auto trial initialisation failed:', err)
        Sentry.captureException(err, {
          tags: { area: 'subscription', operation: 'auto_trial_init' },
        })
      })
  }, [loading, fetchError, sub, orgId, fetchSubscription])

  return useMemo(() => {
    // Fail securely if subscription status cannot be verified.
    if (fetchError) {
      return {
        planTier: 'free_trial' as SubscriptionTier,
        status: 'verification_error',
        isTrial: false,
        trialDaysRemaining: 0,
        isTrialExpired: true,
        isActive: false,
        canCreateEdit: false,
        limits: { users: 1, products: 1, documents: 10, ai_tokens: 0 },
        loading,
        cancelAtPeriodEnd: false,
        currentPeriodEnd: null,
        isAdmin: false,
        refresh: fetchSubscription,
      }
    }

    const planTier = (sub?.plan_tier ?? 'free_trial') as SubscriptionTier
    const status = sub?.status ?? 'trialing'
    const isTrial = planTier === 'free_trial' || status === 'trialing'
    const trialEnd = sub?.trial_ends_at ? new Date(sub.trial_ends_at) : null
    const now = new Date()
    const trialDaysRemaining = trialEnd
      ? Math.max(0, Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)))
      : 0
    const isTrialExpired = isTrial && trialEnd !== null && trialEnd < now
    const isActive = status === 'active' || (status === 'trialing' && !isTrialExpired)
    const canCreateEdit = isActive && !isTrialExpired

    return {
      planTier,
      status,
      isTrial,
      trialDaysRemaining,
      isTrialExpired,
      isActive,
      canCreateEdit,
      limits: getPlanLimits(planTier),
      loading,
      cancelAtPeriodEnd: sub?.cancel_at_period_end ?? false,
      currentPeriodEnd: sub?.current_period_end ?? null,
      isAdmin: false,
      refresh: fetchSubscription,
    }
  }, [sub, loading, fetchError, fetchSubscription])
}
